import Papa from 'papaparse';
import { googleSheets } from '../config';

/**
 * Fetches and parses the odds data from the Odds.csv file
 * @returns {Promise<Array>} Array of odds objects
 */
export const fetchOddsData = async () => {
  try {
    const response = await fetch('/Odds.csv');
    const csvText = await response.text();
    
    const results = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true
    });
    
    // Filter for AFL odds only
    const nrlOdds = results.data.filter(odds => odds.Sport === 'NRL');
    return nrlOdds;
  } catch (error) {
    console.error('Error fetching odds data:', error);
    return [];
  }
};

/**
 * Fetches and parses the odds data from Google Sheets
 * @param {String} sheetId - The Google Sheet ID
 * @param {String} sheetName - The name or gid of the specific sheet
 * @returns {Promise<Array>} Array of odds objects
 */
export const fetchOddsFromGoogleSheets = async (
  sheetId = googleSheets.oddsSheetId, 
  sheetName = googleSheets.oddsSheetGid
) => {
  try {
    // Construct the URL to fetch the CSV data from Google Sheets
    const url = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${sheetName}`;
    
    console.log('Fetching odds from:', url);
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Google Sheets response error (${response.status}): ${errorText}`);
      throw new Error(`Failed to fetch Google Sheet: ${response.status} ${response.statusText}`);
    }
    
    const csvText = await response.text();
    console.log('Received CSV data:', csvText.slice(0, 200) + '...'); // Log first 200 chars
    
    const results = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true
    });
    
    if (results.errors && results.errors.length > 0) {
      console.warn('CSV parsing had errors:', results.errors);
    }
    
    // Filter for NRL odds only if the Sport column exists
    const nrlOdds = results.data.filter(odds => 
      !odds.Sport || odds.Sport === 'NRL'
    );
    
    console.log('Parsed NRL odds:', {
      totalRows: nrlOdds.length,
      sampleData: nrlOdds.slice(0, 3)
    });
    
    return nrlOdds;
  } catch (error) {
    console.error('Error fetching odds data from Google Sheets:', error);
    throw error;
  }
};

/**
 * Gets odds for a specific player and stat
 * @param {Array} oddsData - Array of odds objects
 * @param {String} playerName - Player name
 * @param {String} statType - Stat type (e.g., 'disposals', 'goals')
 * @returns {Array} Array of odds objects for the player and stat
 */
export const getPlayerOdds = (oddsData, playerName, statType) => {
  if (!oddsData || !playerName || !statType) {
    console.log('Missing required data:', { oddsData: !!oddsData, playerName, statType });
    return [];
  }
  
  const convertedStatType = convertStatCategoryForOdds(statType);
  
  // Clean up player name for matching (remove extra spaces, etc)
  const cleanPlayerName = playerName.trim().toLowerCase();
  
  // Filter odds for the player and stat
  const filteredOdds = oddsData.filter(odds => {
    if (!odds.Player || !odds.Stat) return false;
    
    const oddsPlayerName = odds.Player.trim().toLowerCase();
    const oddsStatType = odds.Stat.trim().toLowerCase();
    
    const playerMatch = oddsPlayerName.includes(cleanPlayerName) || 
                       cleanPlayerName.includes(oddsPlayerName);
    const statMatch = oddsStatType === convertedStatType.toLowerCase();
    
    // Debug logging for near matches
    if (playerMatch && !statMatch) {
      console.log('Player found but stat mismatch:', {
        player: odds.Player,
        expectedStat: convertedStatType,
        actualStat: odds.Stat
      });
    }
    
    return playerMatch && statMatch;
  });
  
  console.log('Filtered odds:', filteredOdds);
  
  // Transform the data into the format expected by the UI
  const transformedOdds = [];
  
  filteredOdds.forEach(odd => {
    // Normalize the line value by removing extra + signs
    const normalizedLine = odd.Line ? odd.Line.replace(/\++/g, '+') : odd.Line;
    
    // For tryscorer odds, we only have over odds
    if (convertedStatType === 'try scorer') {
      transformedOdds.push({
        bookmaker: odd.Bookmaker,
        line: normalizedLine || '1',  // Default to 1 for tryscorer
        type: 'over',
        value: parseFloat(odd['Odds over']).toFixed(2)
      });
    } else {
      // Handle other stat types as before
      if (odd['Odds over'] && parseFloat(odd['Odds over']) > 0) {
        transformedOdds.push({
          bookmaker: odd.Bookmaker,
          line: normalizedLine,
          type: 'over',
          value: parseFloat(odd['Odds over']).toFixed(2)
        });
      }
      
      if (odd['Odds under'] && parseFloat(odd['Odds under']) > 0) {
        transformedOdds.push({
          bookmaker: odd.Bookmaker,
          line: normalizedLine,
          type: 'under',
          value: parseFloat(odd['Odds under']).toFixed(2)
        });
      }
    }
  });
  
  console.log('Transformed odds:', transformedOdds);
  return transformedOdds;
};

/**
 * Gets odds for a specific match
 * @param {Array} oddsData - Array of odds objects
 * @param {String} homeTeam - Home team name
 * @param {String} awayTeam - Away team name
 * @returns {Array} Array of odds objects for the match
 */
export const getMatchOdds = (oddsData, homeTeam, awayTeam) => {
  if (!oddsData || !homeTeam || !awayTeam) return [];
  
  const matchPattern = `${homeTeam} vs ${awayTeam}`;
  const reverseMatchPattern = `${awayTeam} vs ${homeTeam}`;
  
  return oddsData.filter(odds => 
    odds.Match && 
    (odds.Match.includes(matchPattern) || odds.Match.includes(reverseMatchPattern))
  );
};

/**
 * Gets the best odds for a player and stat
 * @param {Array} oddsData - Array of odds objects
 * @param {String} playerName - Player name
 * @param {String} statType - Stat type
 * @param {String} betType - 'over' or 'under'
 * @returns {Object} Best odds object with bookmaker and odds value
 */
export const getBestOdds = (oddsData, playerName, statType, betType) => {
  const playerOdds = getPlayerOdds(oddsData, playerName, statType);
  
  if (playerOdds.length === 0) return null;
  
  // Filter by bet type
  const filteredOdds = playerOdds.filter(odd => odd.type === betType.toLowerCase());
  
  if (filteredOdds.length === 0) return null;
  
  // Find the best odds (highest value)
  return filteredOdds.reduce((best, current) => {
    const currentValue = parseFloat(current.value);
    const bestValue = best ? parseFloat(best.value) : 0;
    
    return currentValue > bestValue ? current : best;
  }, null);
};

/**
 * Converts stat category from app format to odds format
 * @param {String} statCategory - Stat category in app format
 * @returns {String} Stat category in odds format
 */
export const convertStatCategoryForOdds = (statCategory) => {
  switch (statCategory) {
    case 'tries':
      return 'try scorer';  // Match exactly what's in the Google Sheet
    case 'points':
      return 'player points';
    case 'performance_points':
      return 'player performance';
    default:
      return statCategory;
  }
};

/**
 * Debug function to log odds data structure
 * @param {Array} oddsData - Array of odds objects
 */
export const debugOddsData = (oddsData) => {
  if (!oddsData || oddsData.length === 0) {
    console.log('Odds data is empty or undefined');
    return;
  }
  
 
  // Count items by sport
  const sportCounts = {};
  oddsData.forEach(odd => {
    const sport = odd.Sport || 'Unknown';
    sportCounts[sport] = (sportCounts[sport] || 0) + 1;
  });

  
  // Check for NRL player odds
  const nrlPlayerOdds = oddsData.filter(odd => 
    odd.Sport === 'NRL' && odd.Player && odd.Player.trim() !== ''
  );

  
  if (nrlPlayerOdds.length > 0) {
    // Log a sample player
    const samplePlayer = nrlPlayerOdds[0].Player;

    
    // Log stats available for this player
    const playerStats = nrlPlayerOdds
      .filter(odd => odd.Player === samplePlayer)
      .map(odd => odd.Stat)
      .filter((stat, index, self) => stat && self.indexOf(stat) === index);
    

  }
}; 